import {
    connectToIoT,
    MW_ERRORS,
    subscribeOnDataChange,
    subscribeOnHolderConnectedState,
    subscribeOnWebSocketDisconnect,
} from 'iqos-mw';

import iccService from '@/services/icc/iccService';
import mwConnectionService from '@/services/mw/mwConnectionService';
import mwService from '@/services/mw/mwService';
import uamService from '@/services/uam/uamService';
import {selectIsHolderConnected, selectMwChargerData, selectMwHolderData} from '@/state/selectors/mw';
import {
    setConnectingToIot,
    setIsDeviceConnectedToIot,
    setIsDeviceWebSocketDisconnected,
    setIsHolderConnectedToIot,
    setIsHolderWebSocketDisconnected,
} from '@/state/slices/deviceSlice';
import {setHolderConnectionStatus} from '@/state/slices/mwSlice';
import {dispatch, getState} from '@/state/store';

const setupHolderConnectionStateSubscription = () => {
    subscribeOnHolderConnectedState((connectionStatus) => {
        dispatch(setHolderConnectionStatus(connectionStatus));
    });
};

// IoT connection handling
const setupIotConnection = async ({codentify, isHolder, onError, onSuccess, isReconnect, isRetry}) => {
    if (!codentify) return;

    try {
        const uamTokenData = await iccService.fetchUamToken({codentify});

        subscribeOnDataChange({
            onChange: (data) => uamService.updateConnectedUamInfo(isHolder, data),
            isHolder,
        });

        subscribeOnWebSocketDisconnect((isHolder) => {
            if (isHolder) {
                dispatch(setIsHolderWebSocketDisconnected(true));
            } else {
                dispatch(setIsDeviceWebSocketDisconnected(true));
            }
        });

        dispatch(setConnectingToIot({isHolder, value: true}));

        const onErrorHandler = (error) => {
            mwService.validateMwError({
                isRetry,
                error,
                isHolder,
                onError,
                onStartAgainHandler: () => {
                    setupIotConnection({codentify, isHolder, onError, onSuccess, isReconnect, isRetry: true});
                },
            });
        };

        await connectToIoT({
            uamTokenData,
            isHolder,
            onError: onErrorHandler,
            onSuccess,
            isReconnect,
        });
    } catch (error) {
        onError({code: MW_ERRORS.ERROR_IOT_CONNECTION_GENERAL.code});
    }
};

// UAM data handling
const validateDeviceData = (deviceData) => {
    if (!deviceData) {
        throw new Error('No device data found');
    }

    const {codentify} = deviceData.charger || {};

    if (!deviceData.charger?.serial) {
        throw new Error('No device serial number found');
    }

    if (!codentify) {
        throw new Error('No device codentify found');
    }

    return {codentify};
};

const getUamData = async ({deviceData, onError, onSuccess}) => {
    try {
        const {codentify} = validateDeviceData(deviceData);
        const {codentify: holderCodentify} = deviceData.holder || {};

        setupHolderConnectionStateSubscription();

        const onErrorConnectHandler = (error) => {
            //clear all as connected if one of them has error
            dispatch(setIsHolderConnectedToIot(false));
            dispatch(setIsDeviceConnectedToIot(false));
            dispatch(setConnectingToIot({isHolder: false, value: false}));
            dispatch(setConnectingToIot({isHolder: true, value: false}));
            onError(error);
        };

        const {onSuccessHandler, onErrorHandler} = mwService.validateFlowEnded({
            onSuccess,
            onError: onErrorConnectHandler,
            isWaitHolder: !!holderCodentify,
        });

        const onSuccessConnectHandler = (isHolder) => {
            if (isHolder) {
                dispatch(setIsHolderConnectedToIot(true));
            } else {
                dispatch(setIsDeviceConnectedToIot(true));
            }

            dispatch(setConnectingToIot({isHolder, value: false}));
            onSuccessHandler();
        };

        // Connect both charger and holder if available
        await Promise.all([
            setupIotConnection({
                codentify,
                isHolder: false,
                onError: onErrorHandler,
                onSuccess: onSuccessConnectHandler,
            }),
            holderCodentify &&
                setupIotConnection({
                    codentify: holderCodentify,
                    isHolder: true,
                    onError: onErrorHandler,
                    onSuccess: () => onSuccessConnectHandler(true),
                }),
        ]);
    } catch (error) {
        onError({code: MW_ERRORS.ERROR_IOT_CONNECTION_GENERAL.code});
    }
};

const getUamDataForConnectedDevice = ({onError, onSuccess} = {}) => {
    const deviceData = {
        charger: selectMwChargerData(getState()),
        holder: selectMwHolderData(getState()),
    };

    const onErrorHandler = (e) => {
        mwConnectionService.handleConnectionError(e);
        onError?.(e);
    };

    getUamData({
        deviceData,
        onError: onErrorHandler,
        onSuccess,
    });
};

const getUamHolderDataForConnectedDevice = ({onSuccess, onError, isRetry}) => {
    const {codentify: holderCodentify} = selectMwHolderData(getState()) || {};

    const onMwError = () => {
        onError?.();
        dispatch(setConnectingToIot({isHolder: true, value: false}));
    };

    const onErrorHandler = (error) => {
        mwService.validateMwError({
            isRetry,
            error,
            isHolder: true,
            onError: onMwError,
            onStartAgainHandler: () => {
                getUamHolderDataForConnectedDevice({onSuccess, onError, isRetry: true});
            },
        });
    };

    if (!holderCodentify) {
        onErrorHandler();
        return;
    }

    const onSuccessHandler = () => {
        const isHolderConnected = selectIsHolderConnected(getState());

        //check holder state before data set
        if (isHolderConnected) {
            dispatch(setIsHolderConnectedToIot(true));
            onSuccess?.();
        } else {
            onErrorHandler();
        }

        dispatch(setConnectingToIot({isHolder: true, value: false}));
    };

    setupIotConnection({
        codentify: holderCodentify,
        isHolder: true,
        onError: onErrorHandler,
        onSuccess: onSuccessHandler,
        isReconnect: true,
    });
};

export default {
    getUamDataForConnectedDevice,
    getUamHolderDataForConnectedDevice,
};
